/**
 * GTM Analytics Service
 *
 * Provides Google Tag Manager integration for tracking user events
 * and conversions for Product-Led Growth analytics.
 */

// Extend Window interface for dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

// Event types for PLG tracking
export type AnalyticsEventName =
  // Conversion funnel (high priority)
  | 'sign_up'
  | 'login'
  | 'view_pricing'
  | 'begin_checkout'
  | 'purchase'
  // Feature engagement (medium priority)
  | 'inspiration_created'
  | 'document_created'
  | 'ai_message_sent'
  | 'creative_dna_created'
  | 'prompt_created'
  // Standard GA4 events
  | 'page_view';

// Event parameter types
export interface SignUpEventParams {
  method?: string;
}

export interface LoginEventParams {
  method?: string;
}

export interface ViewPricingEventParams {
  current_plan?: string;
  recommended_plan?: string;
}

export interface BeginCheckoutEventParams {
  plan_slug: string;
  billing_period: 'MONTHLY' | 'YEARLY';
  value?: number;
  currency?: string;
}

export interface PurchaseEventParams {
  plan_slug: string;
  value?: number;
  currency?: string;
}

export interface InspirationCreatedEventParams {
  type?: string;
  has_tags?: boolean;
}

export interface DocumentCreatedEventParams {
  from_inspirations?: boolean;
}

export interface AIMessageSentEventParams {
  has_persona?: boolean;
  has_product?: boolean;
  model?: string;
}

export interface CreativeDNACreatedEventParams {
  type: 'persona' | 'produto' | 'voz';
}

export interface PromptCreatedEventParams {
  is_favorite?: boolean;
}

export interface PageViewEventParams {
  page_path?: string;
  page_title?: string;
}

// Union type for all event params
export type AnalyticsEventParams =
  | SignUpEventParams
  | LoginEventParams
  | ViewPricingEventParams
  | BeginCheckoutEventParams
  | PurchaseEventParams
  | InspirationCreatedEventParams
  | DocumentCreatedEventParams
  | AIMessageSentEventParams
  | CreativeDNACreatedEventParams
  | PromptCreatedEventParams
  | PageViewEventParams
  | Record<string, unknown>;

/**
 * Initialize Google Tag Manager
 * Should be called once on app startup
 */
export function initGTM(): void {
  const gtmId = import.meta.env.VITE_GTM_ID;

  if (!gtmId) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] VITE_GTM_ID not configured. GTM tracking disabled.');
    }
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // Inject GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);

  if (import.meta.env.DEV) {
    console.log('[Analytics] GTM initialized with ID:', gtmId);
  }
}

/**
 * Track an analytics event
 */
export function trackEvent(name: AnalyticsEventName, params?: AnalyticsEventParams): void {
  if (!window.dataLayer) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] dataLayer not initialized. Event not tracked:', name);
    }
    return;
  }

  window.dataLayer.push({
    event: name,
    ...params,
  });

  if (import.meta.env.DEV) {
    console.log('[Analytics] Event tracked:', name, params);
  }
}

/**
 * Hash a string using SHA-256 (for Facebook Pixel Advanced Matching)
 */
async function hashSHA256(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Identify a user for cross-device tracking
 * Sends user_id to GTM for GA4 User-ID feature
 * Also sends hashed email for Facebook Pixel Advanced Matching
 */
export async function identifyUser(
  userId: string,
  traits?: { email?: string; name?: string; [key: string]: unknown }
): Promise<void> {
  if (!window.dataLayer) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] dataLayer not initialized. User not identified.');
    }
    return;
  }

  // Prepare user data for Facebook Pixel Advanced Matching
  const userData: Record<string, unknown> = {
    event: 'identify',
    user_id: userId,
  };

  // Hash email for Facebook Pixel (Advanced Matching)
  if (traits?.email) {
    try {
      userData.em = await hashSHA256(traits.email);
    } catch {
      // crypto.subtle may not be available in insecure contexts
    }
  }

  // Hash name if provided (fn = first name for FB)
  if (traits?.name) {
    try {
      userData.fn = await hashSHA256(traits.name.split(' ')[0]);
    } catch {
      // Ignore hashing errors
    }
  }

  // Include any other traits
  if (traits) {
    const { email, name, ...otherTraits } = traits;
    Object.assign(userData, otherTraits);
  }

  window.dataLayer.push(userData);

  if (import.meta.env.DEV) {
    console.log('[Analytics] User identified:', userId, userData);
  }
}

/**
 * Track page view - should be called on route changes
 */
export function trackPageView(path?: string, title?: string): void {
  trackEvent('page_view', {
    page_path: path || window.location.pathname,
    page_title: title || document.title,
  });
}
